import { Box, Image } from "@chakra-ui/react";
import { useState } from "react";

interface FeedCardHaloProps {
  // Image URL to derive the halo color from. Loaded with onError → swap
  // off, so a broken image just yields no halo (the card's own bg remains).
  src: string | null | undefined;
  // How far the halo extends past the card edge on every side, in px.
  // Default keeps a clear gutter between adjacent cards.
  bleed?: number;
  // Blur radius applied to the image. Larger = softer glow.
  blur?: number;
}

/**
 * Edge-halo backdrop — a heavily blurred + saturated copy of an image
 * that sits behind the card and bleeds slightly past its edges. The card
 * itself stays opaque (via its own bg + position:relative + zIndex), so
 * text on the card never has to fight the halo for contrast.
 *
 * Drop this as a sibling BEFORE the card content; the consuming wrapper
 * needs `position="relative"` and the card content needs zIndex >= 1 so
 * it stacks above the halo.
 */
export function FeedCardHalo({ src, bleed = 8, blur = 20 }: FeedCardHaloProps) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <Box
      position="absolute"
      top={`-${bleed}px`}
      left={`-${bleed}px`}
      right={`-${bleed}px`}
      bottom={`-${bleed}px`}
      overflow="hidden"
      borderRadius="3xl"
      pointerEvents="none"
      zIndex={0}
    >
      <Image
        src={src}
        alt=""
        w="100%"
        h="100%"
        objectFit="cover"
        sx={{ filter: `blur(${blur}px) saturate(1.6) brightness(0.85)` }}
        onError={() => setFailed(true)}
      />
    </Box>
  );
}
